import { BehaviorSubject, filter } from 'rxjs';
import { MemoizeExpiring } from 'typescript-memoize';
import { FSAction, GitAction } from './actions/actions.enum';
import type { FSDeleteFileAction, FSDeleteFileActionFn } from './actions/fs-delete-file';
import type { FSInitAction, FSInitActionFn } from './actions/fs-init';
import type { FSMkDirAction, FSMkDirActionFn } from './actions/fs-mk-dir';
import type { FSReadDirAction, FSReadDirActionFn } from './actions/fs-read-dir';
import type { FSReadFileAction, FSReadFileActionFn } from './actions/fs-read-file';
import type { FSReadFilesAction, FSReadFilesActionFn } from './actions/fs-read-files';
import type { FSReadJSONAction, FSReadJSONActionFn } from './actions/fs-read-json';
import type { FSReadYAMLAction, FSReadYAMLActionFn } from './actions/fs-read-yaml';
import type { FSRenameEntryAction, FSRenameEntryActionFn } from './actions/fs-rename-entry';
import type { FSRmDirAction, FSRmDirActionFn } from './actions/fs-rm-dir';
import type { FSSearchAction, FSSearchActionFn } from './actions/fs-search';
import type { FSStatAction, FSStatActionFn } from './actions/fs-stat';
import type { FSSyncAction, FSSyncActionFn } from './actions/fs-sync';
import type { FSWriteFileAction, FSWriteFileActionFn } from './actions/fs-write-file';
import type { FSWriteFilesAction, FSWriteFilesActionFn } from './actions/fs-write-files';
import type { FSWriteJSONAction, FSWriteJSONActionFn } from './actions/fs-write-json';
import type { FSWriteYAMLAction, FSWriteYAMLActionFn } from './actions/fs-write-yaml';
import type { GitAddAction, GitAddActionFn } from './actions/git-add';
import type { GitBlameAction, GitBlameActionFn } from './actions/git-blame';
import type { GitBranchAction, GitBranchActionFn } from './actions/git-branch';
import type { GitBranchListAction, GitBranchListActionFn } from './actions/git-branch-list';
import type { GitBranchShowCurrentAction, GitBranchShowCurrentActionFn } from './actions/git-branch-show-current';
import type { GitCallAction, GitCallActionFn } from './actions/git-call';
import type { GitCheckoutAction, GitCheckoutActionFn } from './actions/git-checkout';
import type { GitCloneAction, GitCloneActionFn } from './actions/git-clone';
import type { GitCommitAction, GitCommitActionFn } from './actions/git-commit';
import type { GitDiffAction, GitDiffActionFn } from './actions/git-diff/git-diff';
import type { GitFetchAction, GitFetchActionFn } from './actions/git-fetch';
import type { GitForEachRefAction, GitForEachRefActionFn } from './actions/git-for-each-ref';
import type { GitLogAction, GitLogActionFn } from './actions/git-log';
import type { GitLsRemoteAction, GitLsRemoteActionFn } from './actions/git-ls-remote';
import type { GitMergeAction, GitMergeActionFn } from './actions/git-merge';
import { GitMergeBaseAction, GitMergeBaseActionFn } from './actions/git-merge-base';
import type { GitMergeTreeAction, GitMergeTreeActionFn } from './actions/git-merge-tree';
import type { GitPullAction, GitPullActionFn } from './actions/git-pull';
import type { GitPushAction, GitPushActionFn } from './actions/git-push';
import type { GitResetAction, GitResetActionFn } from './actions/git-reset';
import type { GitRevListAction, GitRevListActionFn } from './actions/git-rev-list';
import type { GitRevParseAction, GitRevParseActionFn } from './actions/git-rev-parse';
import type { GitRevertAction, GitRevertActionFn } from './actions/git-revert';
import type { GitShowAction, GitShowActionFn } from './actions/git-show';
import type { GitStashAction, GitStashActionFn } from './actions/git-stash';
import type { GitStatusAheadBehindAction, GitStatusAheadBehindActionFn } from './actions/git-status-ahead-behind';
import type { GitStatusShortAction, GitStatusShortActionFn } from './actions/git-status-short';
import { ErrorCodeToErrorClass } from './all-errors';
import type { GitError, IGitError } from './wasm-git-worker-errors.model';
import type { WorkerAction, WorkerActionParams, WorkerFnParams, WorkerResult } from './wasm-git.worker.model';

export class WasmGitService {
  constructor(private readonly worker: Worker) {
    this.worker.onmessage = (msg) => {
      if (!msg.data.id) {
        console.error(`[WasmGitService]: msg.data.id undefined`);
        return;
      }

      if (msg.data.error) {
        this.resolverMap[msg.data.id]?.cb({ id: msg.data.id, action: msg.data.action?.type, error: msg.data.error });
      } else {
        this.resolverMap[msg.data.id]?.cb(msg.data.response);
      }

      delete this.resolverMap[msg.data.id];
    };
  }

  private workerMessageId = 1;
  private readonly resolverMap: Record<
    number,
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cb: (result: WorkerResult<any>) => void;
      startTime: number;
    }
  > = {};

  private readonly fsInitialized$$ = new BehaviorSubject(false);
  readonly fsInitialized$ = this.fsInitialized$$.pipe(filter((initialized) => initialized));

  async initFS(params: WorkerActionParams<FSInitAction>) {
    await this.call<FSInitAction, typeof FSInitActionFn>({ type: FSAction.Init, params });
    this.fsInitialized$$.next(true);
  }

  async mkDir(params: WorkerActionParams<FSMkDirAction>) {
    return await this.call<FSMkDirAction, typeof FSMkDirActionFn>({ type: FSAction.MkDir, params });
  }

  async rmDir(params: WorkerActionParams<FSRmDirAction>) {
    return await this.call<FSRmDirAction, typeof FSRmDirActionFn>({ type: FSAction.RmDir, params });
  }

  async readDir(params?: WorkerActionParams<FSReadDirAction>) {
    return await this.call<FSReadDirAction, typeof FSReadDirActionFn>({ type: FSAction.ReadDir, params });
  }

  async readFile(params: WorkerActionParams<FSReadFileAction>) {
    return await this.call<FSReadFileAction, typeof FSReadFileActionFn>({ type: FSAction.ReadFile, params });
  }

  async readFiles(params: WorkerActionParams<FSReadFilesAction>) {
    return await this.call<FSReadFilesAction, typeof FSReadFilesActionFn>({ type: FSAction.ReadFiles, params });
  }

  async readJSON(params: WorkerActionParams<FSReadJSONAction>) {
    return await this.call<FSReadJSONAction, typeof FSReadJSONActionFn>({ type: FSAction.ReadJSON, params });
  }

  async readYAML(params: WorkerActionParams<FSReadYAMLAction>) {
    return await this.call<FSReadYAMLAction, typeof FSReadYAMLActionFn>({ type: FSAction.ReadYAML, params });
  }

  async renameEntry(params: WorkerActionParams<FSRenameEntryAction>) {
    return await this.call<FSRenameEntryAction, typeof FSRenameEntryActionFn>({ type: FSAction.RenameEntry, params });
  }

  async writeFile(params: WorkerActionParams<FSWriteFileAction>) {
    return await this.call<FSWriteFileAction, typeof FSWriteFileActionFn>({ type: FSAction.WriteFile, params });
  }

  async writeFiles(params: WorkerActionParams<FSWriteFilesAction>) {
    return await this.call<FSWriteFilesAction, typeof FSWriteFilesActionFn>({ type: FSAction.WriteFiles, params });
  }

  async writeJSON(params: WorkerActionParams<FSWriteJSONAction>) {
    return await this.call<FSWriteJSONAction, typeof FSWriteJSONActionFn>({ type: FSAction.WriteJSON, params });
  }

  async writeYAML(params: WorkerActionParams<FSWriteYAMLAction>) {
    return await this.call<FSWriteYAMLAction, typeof FSWriteYAMLActionFn>({ type: FSAction.WriteYAML, params });
  }

  async deleteFile(params: WorkerActionParams<FSDeleteFileAction>) {
    return await this.call<FSDeleteFileAction, typeof FSDeleteFileActionFn>({ type: FSAction.DeleteFile, params });
  }

  async syncFS() {
    return await this.call<FSSyncAction, typeof FSSyncActionFn>({ type: FSAction.Sync });
  }

  async search(params: WorkerActionParams<FSSearchAction>) {
    return await this.call<FSSearchAction, typeof FSSearchActionFn>({ type: FSAction.Search, params });
  }

  async statFS(params: WorkerActionParams<FSStatAction>) {
    return await this.call<FSStatAction, typeof FSStatActionFn>({ type: FSAction.Stat, params });
  }

  async gitAdd(params?: WorkerActionParams<GitAddAction>) {
    return await this.call<GitAddAction, typeof GitAddActionFn>({ type: GitAction.Add, params });
  }

  async gitBranch(params: WorkerActionParams<GitBranchAction>) {
    return await this.call<GitBranchAction, typeof GitBranchActionFn>({ type: GitAction.Branch, params });
  }

  async gitBranchList() {
    return await this.call<GitBranchListAction, typeof GitBranchListActionFn>({ type: GitAction.BranchList });
  }

  async gitBranchShowCurrent() {
    return await this.call<GitBranchShowCurrentAction, typeof GitBranchShowCurrentActionFn>({
      type: GitAction.BranchShowCurrent,
    });
  }

  async gitCheckout(params: WorkerActionParams<GitCheckoutAction>) {
    return await this.call<GitCheckoutAction, typeof GitCheckoutActionFn>({ type: GitAction.Checkout, params });
  }

  async gitClone(params: WorkerActionParams<GitCloneAction>) {
    return await this.call<GitCloneAction, typeof GitCloneActionFn>({ type: GitAction.Clone, params });
  }

  async gitCommit(params: WorkerActionParams<GitCommitAction>) {
    return await this.call<GitCommitAction, typeof GitCommitActionFn>({ type: GitAction.Commit, params });
  }

  async gitFetch(params: WorkerActionParams<GitFetchAction>) {
    return await this.call<GitFetchAction, typeof GitFetchActionFn>({ type: GitAction.Fetch, params });
  }

  async gitMerge(params: WorkerActionParams<GitMergeAction>) {
    return await this.call<GitMergeAction, typeof GitMergeActionFn>({ type: GitAction.Merge, params });
  }

  async gitMergeBase(params: WorkerActionParams<GitMergeBaseAction>) {
    return await this.call<GitMergeBaseAction, typeof GitMergeBaseActionFn>({ type: GitAction.MergeBase, params });
  }

  async gitMergeTree(params: WorkerActionParams<GitMergeTreeAction>) {
    return await this.call<GitMergeTreeAction, typeof GitMergeTreeActionFn>({ type: GitAction.MergeTree, params });
  }

  async gitPush(params: WorkerActionParams<GitPushAction>) {
    return await this.call<GitPushAction, typeof GitPushActionFn>({ type: GitAction.Push, params });
  }

  // getFileStatutes & getConflictedFiles both call this method in quick succession
  // TODO alex optimized getConflictedFiles method in GitService
  @MemoizeExpiring(100)
  async gitStatusShort() {
    return await this.call<GitStatusShortAction, typeof GitStatusShortActionFn>({ type: GitAction.StatusShort });
  }

  async gitStatusAheadBehind() {
    return await this.call<GitStatusAheadBehindAction, typeof GitStatusAheadBehindActionFn>({
      type: GitAction.StatusAheadBehind,
    });
  }

  async gitShow(params: WorkerActionParams<GitShowAction>) {
    return await this.call<GitShowAction, typeof GitShowActionFn>({ type: GitAction.Show, params });
  }

  async gitReset(params?: WorkerActionParams<GitResetAction>) {
    return await this.call<GitResetAction, typeof GitResetActionFn>({ type: GitAction.Reset, params });
  }

  async gitRevert(params: WorkerActionParams<GitRevertAction>) {
    return await this.call<GitRevertAction, typeof GitRevertActionFn>({ type: GitAction.Revert, params });
  }

  async gitPull(params: WorkerActionParams<GitPullAction>) {
    return await this.call<GitPullAction, typeof GitPullActionFn>({ type: GitAction.Pull, params });
  }

  async gitStash(params?: WorkerActionParams<GitStashAction>) {
    return await this.call<GitStashAction, typeof GitStashActionFn>({ type: GitAction.Stash, params });
  }

  async gitLog(params?: WorkerActionParams<GitLogAction>) {
    return await this.call<GitLogAction, typeof GitLogActionFn>({ type: GitAction.Log, params });
  }

  async gitDiff(params: WorkerActionParams<GitDiffAction>) {
    return await this.call<GitDiffAction, typeof GitDiffActionFn>({ type: GitAction.Diff, params });
  }

  async gitBlame(params: WorkerActionParams<GitBlameAction>) {
    return await this.call<GitBlameAction, typeof GitBlameActionFn>({ type: GitAction.Blame, params });
  }

  async gitRevList(params: WorkerActionParams<GitRevListAction>) {
    return await this.call<GitRevListAction, typeof GitRevListActionFn>({ type: GitAction.RevList, params });
  }

  async gitRevParse(params: WorkerActionParams<GitRevParseAction>) {
    return await this.call<GitRevParseAction, typeof GitRevParseActionFn>({ type: GitAction.RevParse, params });
  }

  async gitForEachRef() {
    return await this.call<GitForEachRefAction, typeof GitForEachRefActionFn>({ type: GitAction.ForEachRef });
  }

  async gitLsRemote(params: WorkerActionParams<GitLsRemoteAction>) {
    return await this.call<GitLsRemoteAction, typeof GitLsRemoteActionFn>({ type: GitAction.LsRemote, params });
  }

  async gitCall(command: string) {
    // this regexp is used here to support parameters with spaces,
    // make sure you wrap such parameters in quotes,
    // e.g. y42.git('rev-parse "kind-of-working:src/00-integrations/Google Ads/metadata.json"')
    // taken from https://stackoverflow.com/questions/2817646/javascript-split-string-on-space-or-on-quotes-to-array/18647776
    const params: WorkerActionParams<GitCallAction> = {
      commands: (command.match(/[^\s"]+|"([^"]*)"/gi) || []).map((cmd) => cmd.replace(/"/g, '')),
    };

    return this.call<GitCallAction, typeof GitCallActionFn>({ type: GitAction.Call, params });
  }

  async call<Action extends WorkerAction, Fn extends (params: WorkerFnParams<Action>) => ReturnType<Fn>>(
    action: Action,
  ): Promise<ReturnType<Fn>> {
    const ignoredActionTypes: string[] = [FSAction.ReadJSON];

    if (!ignoredActionTypes.includes(action.type)) {
      if (!(action as WorkerAction).params) {
        console.debug(`[WasmGit]: ${action.type}`);
      } else {
        console.debug(`[WasmGit]: ${action.type}`, (action as WorkerAction).params);
      }
    }

    try {
      const value = await new Promise((resolve, reject) => {
        const id = this.workerMessageId++;

        this.resolverMap[id] = {
          startTime: this.getNow(),
          cb: (result) => {
            const startTime = result?.id ? this.resolverMap[result.id]?.startTime : this.getNow();
            const duration = Math.round(this.getNow() - (startTime || 0));

            if (duration > 100 && !ignoredActionTypes.includes(action.type)) {
              console[result.error ? 'error' : 'debug'](
                `[WasmGit]: ${action.type} ${result.error ? 'errored' : 'finished'} after ${duration}ms`,
              );
            }

            if (result && result.error) {
              reject(result.error);
            } else {
              resolve(result as ReturnType<Fn>);
            }
          },
        };
        this.worker.postMessage({ action, id });
      });

      return value as ReturnType<Fn>;
    } catch (err) {
      const error = err as IGitError | Error;
      const message: string = error.message;

      console.error(err);

      if ('gitErrorCode' in error) {
        const gitErrorClass = ErrorCodeToErrorClass[error.gitErrorCode];
        const gitError = new gitErrorClass(message, false);
        gitError.type = action.type;
        gitError.params = action.params;
        gitError.path = (gitError.params as GitError)?.path;
        throw gitError;
      }

      throw err;
    }
  }

  private getNow() {
    return performance?.now?.() || Date.now();
  }
}
